import React, { useState } from "react";
import { useParams } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "../../../app/store";

import {
  EntityType,
  RelatedAs,
} from "../../../api/stakeholder_relationships";

import { createStakeholders } from "../logic/stakeholderThunks";
import { showErrorToast, showSuccessToast } from "../../../lib/toast";
import { getAccountProductInfoByAccountNumber } from "../../creditAccounts/logic/slice";

import ManagementFormField from "./ManagementFormField";
import ManagementModal from "../../../components/ui/modals/ManagementModal";

interface AddNewGuarantorModalProps {
  accountNumber: string;
  onClose: () => void;
}

interface FormState {
  name: string;
  cifNumber: string;
}

const AddNewGuarantorModal: React.FC<AddNewGuarantorModalProps> = ({
  accountNumber,
  onClose,
}) => {
  const { id: caseId } = useParams<{ id: string }>();

  const dispatch = useAppDispatch();
  const displayAccountNumber = useAppSelector(
    (state) =>
      getAccountProductInfoByAccountNumber(state, accountNumber)
        ?.ext_account_number || "--",
  );

  const [form, setForm] = useState<FormState>({
    name: "",
    cifNumber: "",
  });

  const [isLoading, setIsLoading] = useState(false);

  const handleChange = <K extends keyof FormState>(
    field: K,
    value: FormState[K],
  ) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const isFormValid = form.name.trim().length > 0;

  const handleSubmit = async () => {
    if (!caseId || !isFormValid || isLoading) {
      return;
    }

    try {
      setIsLoading(true);

      await dispatch(
        createStakeholders({
          caseId,
          request: {
            stakeholders: [
              {
                stakeholder: {
                  name: form.name.trim(),
                },
                relationships: [
                  {
                    entity_type: EntityType.LOAN_ACCOUNT,
                    entity_id: accountNumber,
                    related_as: RelatedAs.GUARANTOR,
                  },
                ],
                customer_details: form.cifNumber.trim()
                  ? {
                      cif_number: form.cifNumber.trim(),
                    }
                  : undefined,
              },
            ],
          },
        }),
      ).unwrap();
      showSuccessToast("Guarantor added successfully.");
      onClose();
    } catch {
      showErrorToast("Failed to add guarantor. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (isLoading) {
      return;
    }

    onClose();
  };

  return (
    <ManagementModal
      title="Add Guarantor"
      subtitle={displayAccountNumber}
      subtitleClassName="
        text-[12px]
        leading-4
        text-[#737373]
      "
      onSubmit={handleSubmit}
      onClose={handleClose}
      submitLabel="Add"
      submitEnabled={isFormValid}
      isLoading={isLoading}
      className="w-[661px]"
    >
      <div className="grid grid-cols-2 gap-4">
        <ManagementFormField
          label="Full Name"
          value={form.name}
          onChange={(value) => handleChange("name", value)}
          required
          disabled={isLoading}
        />

        <ManagementFormField
          label="CIF Number"
          value={form.cifNumber}
          onChange={(value) => handleChange("cifNumber", value)}
          disabled={isLoading}
        />
      </div>
    </ManagementModal>
  );
};

export default AddNewGuarantorModal;